import { createApi } from "@reduxjs/toolkit/query/react";
import { buildBaseQuery } from "@app/api";
import { FilterParams } from "types";

import { ICategory, CategoryCreationPayload } from "../types/index";

export const categoryApi = createApi({
  reducerPath: "categoryApi",
  baseQuery: buildBaseQuery(""),
  tagTypes: ["Category"],
  endpoints: (builder) => ({
    getCategories: builder.query<{ categories: ICategory[]; count: number }, FilterParams | void>({
      query: (params) => {
        if (!params) {
          return "/categories/dashboard";
        }

        const queryString = Object.entries(params)
          .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
          .join("&");

        return `/categories/dashboard?${queryString}`;
      },
      providesTags: ["Category"],
    }),
    getCategoryById: builder.query<ICategory, string>({
      query: (id) => `/categories/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Category", id }],
    }),
    createCategory: builder.mutation<ICategory, CategoryCreationPayload>({
      query: (body) => ({
        url: "/categories",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Category"],
    }),
    updateCategory: builder.mutation<ICategory, { id: string; body: CategoryCreationPayload }>({
      query: ({ id, body }) => ({
        url: `/categories/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Category", id }, "Category"],
    }),
    deleteCategory: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Category"],
    }),
    sortCategories: builder.mutation<void, { list: { id: string; index: number }[] }>({
      query: (body) => ({
        url: "/categories/sort",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useLazyGetCategoriesQuery,
  useGetCategoryByIdQuery,
  useLazyGetCategoryByIdQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useSortCategoriesMutation,
} = categoryApi;
